// ----------------------------------------------------------------------------
// Home. Quick actions, four stats (with sparklines built from real history),
// recent activity that links to its source, the team's next services and the
// most-played songs.
// ----------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useAuth } from '@app/auth/AuthContext';
import { useChurch } from '@admin/shared/contexts/ChurchContext';
import { useGroup } from '@admin/shared/contexts/GroupContext';
import { membershipsApi, plansApi, songsApi } from '@app/api/organization';
import type { EnrichedMembership, ServicePlan, Song } from '@app/types';
import { useLiveNotifications } from '@app/notifications/LiveNotifications';
import { Avatar, Card, ErrorState, StatusPill, cx } from '@app/components/ui';
import { isPastDate, relativeWhen, shortDate, time12, weekdayShort } from '@app/lib/format';
import { logger } from '@app/utils/logger';

const greeting = (d = new Date()) => (d.getHours() < 12 ? 'Good morning' : d.getHours() < 18 ? 'Good afternoon' : 'Good evening');

/** 96×36 sparkline points, matching the prototype's geometry. */
const sparkPoints = (v: number[]) => {
  if (v.length < 2) return '';
  const mn = Math.min(...v);
  const mx = Math.max(...v);
  const r = mx - mn || 1;
  return v.map((x, i) => `${(i * (92 / (v.length - 1)) + 2).toFixed(1)},${(32 - ((x - mn) / r) * 28).toFixed(1)}`).join(' ');
};

const monthKey = (d: Date) => d.getFullYear() * 12 + d.getMonth();

/** Count per month over the last `n` months (oldest first). */
const perMonth = (dates: Date[], n = 7, now = new Date()) => {
  const end = monthKey(now);
  const out = Array.from({ length: n }, () => 0);
  for (const d of dates) {
    const i = n - 1 - (end - monthKey(d));
    if (i >= 0 && i < n) out[i]++;
  }
  return out;
};

/** Running total per month over the last `n` months. */
const cumulative = (dates: Date[], n = 7, now = new Date()) => {
  const end = monthKey(now);
  return Array.from({ length: n }, (_, i) => dates.filter((d) => monthKey(d) <= end - (n - 1 - i)).length);
};

const fillOf = (p: ServicePlan) => {
  const slots = p.team.length;
  const confirmed = p.team.filter((t) => t.status === 1 && !t.isPlaceholder).length;
  const gaps = p.team.some((t) => t.status === 2 || t.isPlaceholder || !t.userId);
  return { slots, confirmed, color: isPastDate(p.date) ? 'var(--t5)' : slots && confirmed === slots ? 'var(--ok)' : gaps ? 'var(--acc)' : 'var(--amb)' };
};

interface Activity {
  id: string;
  who: string;
  what: string;
  at: string;
  tag: string;
  tone: 'ok' | 'acc' | 'amb' | 'muted' | 'info';
  go: () => void;
}

export const WorshipPlannerDashboard: React.FC = () => {
  const { user } = useAuth();
  const { church } = useChurch();
  const { selectedGroup, selectedGroupId } = useGroup();
  const navigate = useNavigate();
  const live = useLiveNotifications();
  const churchId = user?.churchId;

  const [members, setMembers] = useState<EnrichedMembership[]>([]);
  const [songs, setSongs] = useState<Song[]>([]);
  const [plans, setPlans] = useState<ServicePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    if (!churchId) return;
    setLoading(true);
    setFailed(false);
    try {
      const [m, s, p] = await Promise.all([
        membershipsApi.getChurchMembers(churchId),
        songsApi.getLibrary(churchId),
        plansApi.getRecent(churchId, 100, selectedGroupId ?? undefined),
      ]);
      setMembers(m);
      setSongs(s);
      setPlans(p);
    } catch (err) {
      logger.error('Failed to load dashboard data:', err);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [churchId, selectedGroupId]);

  useEffect(() => {
    void load();
  }, [load]);

  const people = useMemo(() => {
    const seen = new Map<string, EnrichedMembership>();
    for (const m of members) if (m.churchState === 2 && !seen.has(m.userId)) seen.set(m.userId, m);
    return Array.from(seen.values());
  }, [members]);

  const upcoming = useMemo(
    () => plans.filter((p) => !isPastDate(p.date)).sort((a, b) => +new Date(a.date) - +new Date(b.date)),
    [plans],
  );
  const next = upcoming[0];

  const stats = useMemo(() => {
    const now = new Date();
    const svcSeries = perMonth(plans.map((p) => new Date(p.date)));
    const songDates = songs.map((s) => new Date(s.createdAt));
    const songSeries = cumulative(songDates);
    const peopleDates = people.map((p) => new Date(p.joinedAt));
    const peopleSeries = cumulative(peopleDates);
    const thisMonth = (ds: Date[]) => ds.filter((d) => monthKey(d) === monthKey(now)).length;
    const nextFill = next ? fillOf(next) : null;
    const sign = (n: number) => (n > 0 ? `+${n}` : n < 0 ? `${n}` : '');
    return [
      {
        label: 'Services this month',
        value: String(svcSeries[svcSeries.length - 1]),
        delta: sign(svcSeries[svcSeries.length - 1] - svcSeries[svcSeries.length - 2]),
        sub: selectedGroup?.name ?? 'All teams',
        series: svcSeries,
      },
      { label: 'Songs in library', value: String(songs.length), delta: sign(thisMonth(songDates)), sub: 'added this month', series: songSeries },
      {
        label: 'People',
        value: String(people.length),
        delta: sign(thisMonth(peopleDates)),
        sub: live.pendingCount ? `${live.pendingCount} request${live.pendingCount === 1 ? '' : 's'} waiting` : 'joined this month',
        series: peopleSeries,
      },
      {
        label: 'Next service',
        value: nextFill ? `${nextFill.confirmed}/${nextFill.slots}` : '–',
        delta: '',
        sub: next ? `confirmed · ${shortDate(next.date)}` : 'Nothing planned yet',
        series: upcoming.slice(0, 7).map((p) => fillOf(p).confirmed),
        muted: true,
      },
    ];
  }, [plans, songs, people, upcoming, next, selectedGroup, live.pendingCount]);

  const activity = useMemo<Activity[]>(() => {
    const items: Activity[] = [];
    for (const n of live.items) {
      if (n.kind === 'request') {
        items.push({ id: n.id, who: n.who, what: 'asked to join your church', at: n.at, tag: 'Request', tone: 'acc', go: () => navigate('/admin/membership-requests') });
      } else if (n.kind === 'assignment') {
        items.push({
          id: n.id,
          who: n.who,
          what: n.what,
          at: n.at,
          tag: 'Roster',
          tone: /declin/i.test(n.what) ? 'acc' : 'ok',
          go: () => navigate(n.planId ? `/admin/roster?plan=${n.planId}` : '/admin/roster'),
        });
      } else {
        items.push({ id: n.id, who: n.who, what: n.what, at: n.at, tag: 'Availability', tone: 'muted', go: () => navigate('/admin/team-availability') });
      }
    }
    const recentCutoff = Date.now() - 30 * 86400e3;
    for (const s of songs) {
      if (new Date(s.createdAt).getTime() > recentCutoff) {
        items.push({ id: `song-${s.id}`, who: s.title, what: 'was added to the song library', at: s.createdAt, tag: 'Song', tone: 'muted', go: () => navigate(`/admin/songs/${s.id}`) });
      }
    }
    for (const p of plans) {
      if (p.isSetlistPublished && p.items.length && new Date(p.updatedAt).getTime() > recentCutoff) {
        items.push({
          id: `set-${p.id}`,
          who: p.title,
          what: `setlist for ${shortDate(p.date)} was published`,
          at: p.updatedAt,
          tag: 'Setlist',
          tone: 'muted',
          go: () => navigate(`/admin/setlists?plan=${p.id}`),
        });
      }
    }
    return items.sort((a, b) => +new Date(b.at) - +new Date(a.at)).slice(0, 6);
  }, [live.items, songs, plans, navigate]);

  const topSongs = useMemo(() => [...songs].filter((s) => (s.timesSung ?? 0) > 0).sort((a, b) => (b.timesSung ?? 0) - (a.timesSung ?? 0)).slice(0, 5), [songs]);
  const maxUses = topSongs[0]?.timesSung ?? 1;

  if (!churchId) {
    return <p className="text-t4 text-center py-16">No church assigned to your account.</p>;
  }

  const firstName = (user?.username || '').split(' ')[0];
  const tiles = [
    { t: 'Plan a service', s: 'Date, team and setlist', g: '+', accent: true, to: '/admin/roster?plan=new' },
    { t: 'Add a song', s: 'To your song library', g: '♪', to: '/admin/songs?add=1' },
    { t: 'Invite your team', s: 'Share an invite code', g: '#', to: '/admin/invite' },
  ];

  return (
    <div>
      <div className="mb-7">
        <div className="text-[13px] text-t4 mb-2">
          {church?.name ?? ''}
          {selectedGroup ? ` · ${selectedGroup.name}` : ''}
        </div>
        <h1 className="wp-h1">
          {greeting()}
          {firstName ? `, ${firstName}` : ''}
        </h1>
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))' }}>
        {tiles.map((q) => (
          <button
            key={q.t}
            type="button"
            onClick={() => navigate(q.to)}
            className="wp-press flex items-center gap-3 w-full text-left p-4 rounded-[18px] border border-line bg-card text-t1 hover:bg-card-h hover:border-line2"
          >
            <span
              aria-hidden="true"
              className={cx('w-11 h-11 rounded-[13px] flex items-center justify-center text-[19px] font-semibold shrink-0', q.accent ? 'bg-acc-bg2 text-acc' : 'bg-fill2 text-t1')}
            >
              {q.g}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-[15px] font-semibold">{q.t}</span>
              <span className="block text-[13px] text-t4 mt-px">{q.s}</span>
            </span>
            <ChevronRight size={18} strokeWidth={2} className="text-t5 shrink-0" aria-hidden="true" />
          </button>
        ))}
      </div>

      {failed ? (
        <ErrorState className="mt-3.5" title="Couldn't load your dashboard" body="Check your connection and try again." onRetry={load} retrying={loading} />
      ) : (
        <>
          <div className="grid gap-3.5 mt-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(230px, 100%), 1fr))' }}>
            {stats.map((st) => (
              <div key={st.label} className="flex items-center justify-between gap-3 rounded-[20px] border border-line bg-card px-5 py-[18px]">
                <div className="min-w-0">
                  <div className="text-[13px] text-t3">{st.label}</div>
                  {loading ? (
                    <span className="wp-skeleton block h-7 w-16 rounded-[8px] mt-1.5" />
                  ) : (
                    <div className="flex items-baseline gap-2 mt-1.5">
                      <span className="font-mono text-[24px] font-bold">{st.value}</span>
                      {st.delta && <span className={cx('text-[12.5px] font-semibold', st.delta.startsWith('-') ? 'text-acc' : 'text-ok')}>{st.delta}</span>}
                    </div>
                  )}
                  <div className="text-[12px] text-t4 mt-[3px] truncate">{st.sub}</div>
                </div>
                {!loading && st.series.length > 1 && (
                  <svg width="96" height="36" viewBox="0 0 96 36" fill="none" aria-hidden="true" className="shrink-0">
                    <polyline
                      points={sparkPoints(st.series)}
                      stroke={st.muted ? 'var(--t4)' : 'var(--acc)'}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                )}
              </div>
            ))}
          </div>

          <div className="grid gap-3.5 mt-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))' }}>
            <Card pad="22px 12px 12px" className="self-start">
              <h2 className="text-[16px] font-semibold mx-2.5 mb-2">Recent activity</h2>
              {loading ? (
                <div className="flex flex-col gap-3 p-2.5" aria-hidden="true">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="flex gap-3 items-center">
                      <span className="wp-skeleton w-[34px] h-[34px] rounded-full" />
                      <span className="wp-skeleton h-3 flex-1 rounded-[6px]" />
                    </div>
                  ))}
                </div>
              ) : activity.length === 0 ? (
                <p className="text-[13.5px] text-t4 px-2.5 py-6">No recent activity yet. Confirmations, join requests and new songs show up here.</p>
              ) : (
                activity.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={a.go}
                    className="flex gap-3 items-start w-full text-left px-2.5 py-3 rounded-[14px] hover:bg-fill text-t1"
                  >
                    <Avatar name={a.who} size={34} />
                    <span className="flex-1 min-w-0 text-[14px] leading-[1.45] text-t2">
                      <span className="text-t1 font-semibold">{a.who}</span> {a.what}
                      <span className="block text-[12px] text-t4 mt-0.5">{relativeWhen(a.at)}</span>
                    </span>
                    <StatusPill tone={a.tone}>{a.tag}</StatusPill>
                    <ChevronRight size={16} strokeWidth={2} className="text-t5 shrink-0 mt-1" aria-hidden="true" />
                  </button>
                ))
              )}
            </Card>

            <div className="flex flex-col gap-3.5">
              <Card>
                <div className="flex justify-between items-center mb-1.5">
                  <h2 className="text-[16px] font-semibold">Coming up</h2>
                  <button type="button" onClick={() => navigate('/admin/roster')} className="text-[13px] text-link hover:text-acc">
                    Roster
                  </button>
                </div>
                {loading ? (
                  <span className="wp-skeleton block h-24 rounded-[14px] mt-2" />
                ) : upcoming.length === 0 ? (
                  <p className="text-[13.5px] text-t4 py-4">
                    No upcoming services for {selectedGroup?.name ?? 'this team'}.{' '}
                    <button type="button" className="text-link" onClick={() => navigate('/admin/roster?plan=new')}>
                      Plan one
                    </button>
                  </p>
                ) : (
                  upcoming.slice(0, 4).map((u) => {
                    const f = fillOf(u);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => navigate(`/admin/roster?plan=${u.id}`)}
                        className="flex w-full text-left bg-transparent p-0 text-t1 group"
                      >
                        <span className="w-12 shrink-0 text-center pt-2.5">
                          <span className="block text-[11px] font-semibold uppercase text-t4">{weekdayShort(u.date)}</span>
                          <span className="block font-mono text-[20px] font-bold">{new Date(u.date).getDate()}</span>
                        </span>
                        <span className="relative flex-1 border-l border-line2 py-3 pl-4">
                          <span
                            aria-hidden="true"
                            className="absolute -left-[4.5px] top-[21px] w-2 h-2 rounded-full"
                            style={{ background: f.color, boxShadow: '0 0 0 4px var(--bg)' }}
                          />
                          <span className="block text-[14.5px] font-medium group-hover:text-acc">{u.title}</span>
                          <span className="block text-[12.5px] text-t4 mt-0.5">
                            {time12(u.date)} · {f.confirmed} of {f.slots} confirmed
                          </span>
                        </span>
                      </button>
                    );
                  })
                )}
              </Card>

              <Card>
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-[16px] font-semibold">Most played</h2>
                  <button type="button" onClick={() => navigate('/admin/songs')} className="text-[13px] text-link hover:text-acc">
                    Songs
                  </button>
                </div>
                {loading ? (
                  <span className="wp-skeleton block h-24 rounded-[14px]" />
                ) : topSongs.length === 0 ? (
                  <p className="text-[13.5px] text-t4">Play counts appear once services with songs have happened.</p>
                ) : (
                  topSongs.map((s, i) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => navigate(`/admin/songs/${s.id}`)}
                      className="grid w-full text-left items-center gap-3 py-2 bg-transparent text-t1 group"
                      style={{ gridTemplateColumns: '18px 1fr auto' }}
                    >
                      <span className="font-mono text-[12px] text-t4">{i + 1}</span>
                      <span className="min-w-0">
                        <span className="block text-[14px] font-medium truncate group-hover:text-acc">{s.title}</span>
                        <span className="block h-1 rounded-full bg-fill2 mt-1.5 overflow-hidden">
                          <span className="block h-full rounded-full bg-acc" style={{ width: `${Math.round(((s.timesSung ?? 0) / maxUses) * 100)}%` }} />
                        </span>
                      </span>
                      <span className="font-mono text-[13px] text-t3" aria-label={`${s.timesSung} plays`}>
                        {s.timesSung}×
                      </span>
                    </button>
                  ))
                )}
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
